import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import InlineCropper from '../InlineCropper';
import BackgroundStyleSelector from './BackgroundStyleSelector';

const SPOTLIGHT_FONTS = [
  { value: 'Barlow Condensed', label: 'Barlow Condensed' },
  { value: 'Bebas Neue', label: 'Bebas Neue' },
  { value: 'Oswald', label: 'Oswald' },
  { value: 'Russo One', label: 'Russo One' },
  { value: 'Black Ops One', label: 'Black Ops One' },
  { value: 'Teko', label: 'Teko' },
  { value: 'Orbitron', label: 'Orbitron' },
];

function HighlightFields({
  formData,
  roster,
  filteredRoster,
  showRosterDropdown,
  saving,
  downloading,
  onPlayerSearch,
  onPlayerSelect,
  onRosterDropdownChange,
  onHighlightStatsChange,
  formatPlayerDisplay,
  canUseCutouts,
  onChange,
  // Dominant image
  dominantImageCropping,
  dominantImageTempImage,
  dominantImagePreview,
  onDominantImageChange,
  onDominantImageCropComplete,
  onDominantImageCropCancel,
  onEditDominantImageCrop,
  onRemoveDominantImage,
  originalDominantImage,
  cutoutDominantImage,
  activeDominantImageVersion,
  setActiveDominantImageVersion,
  onRemoveDominantBg,
  isRemovingDominant,
  dominantBgRemovalError,
  // Background
  backgroundCropping,
  backgroundTempImage,
  backgroundPhotoPreview,
  onBackgroundPhotoChange,
  onBackgroundCropComplete,
  onBackgroundCropCancel,
  onEditBackground,
  onRemoveBackground,
  onSetBackgroundFromUrl,
  backgroundStyle,
  onBackgroundStyleChange,
  teamColors,
}) {
  const [bgMode, setBgMode] = useState(() => {
    if (!backgroundPhotoPreview) return 'team-colors';
    if (backgroundPhotoPreview.includes('pexels.com')) return 'pexels';
    return 'upload';
  });
  const [pexelsQuery, setPexelsQuery] = useState('baseball stadium');
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pexelsLoading, setPexelsLoading] = useState(false);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pexelsTotalResults, setPexelsTotalResults] = useState(0);

  useEffect(() => {
    if (!backgroundPhotoPreview) return;
    if (backgroundPhotoPreview.includes('pexels.com')) setBgMode('pexels');
    else setBgMode('upload');
  }, [backgroundPhotoPreview]);

  async function searchPexels(query, page = 1, append = false) {
    if (append) setPexelsLoadingMore(true);
    else setPexelsLoading(true);
    try {
      const res = await api.get('/pexels/search', { params: { query, page, per_page: 12 } });
      const photos = res.data.photos || [];
      setPexelsTotalResults(res.data.total_results || 0);
      setPexelsPage(page);
      if (append) {
        setPexelsResults((prev) => {
          const combined = [...prev, ...photos];
          return combined.filter((p, i, self) => i === self.findIndex((x) => x.id === p.id));
        });
      } else {
        setPexelsResults(photos);
      }
    } catch (err) {
      console.error('Pexels search failed:', err);
      if (!append) setPexelsResults([]);
    } finally {
      if (append) setPexelsLoadingMore(false);
      else setPexelsLoading(false);
    }
  }

  function handlePexelsSearch() {
    searchPexels(pexelsQuery, 1, false);
  }

  function handlePexelsLoadMore() {
    searchPexels(pexelsQuery, pexelsPage + 1, true);
  }

  return (
    <>
      {/* Player Selection */}
      <div className="form-group player-search-group">
        <label htmlFor="highlightPlayer">
          Select Player *
          {roster.length > 0 && (
            <span
              style={{
                fontSize: '12px',
                color: '#718096',
                fontWeight: 'normal',
                marginLeft: '8px',
              }}
            >
              (Search roster or type custom name)
            </span>
          )}
        </label>
        <div className="player-search-container">
          <input
            id="highlightPlayer"
            name="highlightPlayer"
            type="text"
            value={formData.highlightPlayer}
            onChange={onPlayerSearch}
            onFocus={() => onRosterDropdownChange(true)}
            onBlur={() => {
              setTimeout(() => onRosterDropdownChange(false), 200);
            }}
            placeholder={
              roster.length > 0 ? 'Search roster or type name...' : 'Player Name'
            }
            autoComplete="off"
            required
          />
          {showRosterDropdown && filteredRoster.length > 0 && (
            <div className="roster-dropdown">
              {filteredRoster.map((player) => (
                <div
                  key={player.id}
                  className="roster-dropdown-item"
                  onClick={() => onPlayerSelect(player)}
                >
                  {formatPlayerDisplay(player)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Player Stats/Achievements */}
      <div className="form-group">
        <label htmlFor="highlightStats">
          Stats & Achievements *
          <span
            style={{
              fontSize: '12px',
              color: '#718096',
              fontWeight: 'normal',
              marginLeft: '8px',
            }}
          >
            (Season stats, achievements, highlights) -{' '}
            {formData.highlightStats.length}/200 characters
          </span>
        </label>
        <textarea
          id="highlightStats"
          name="highlightStats"
          value={formData.highlightStats}
          onChange={onHighlightStatsChange}
          placeholder="Player game or season acheivements here."
          rows="4"
          required
          maxLength={200}
        />
      </div>

      {/* Spotlight Label Customization */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Spotlight Label
        </div>
        <div className="form-group">
          <label htmlFor="spotlightLabelFont">Font</label>
          <select
            id="spotlightLabelFont"
            name="spotlightLabelFont"
            value={formData.spotlightLabelFont || 'Barlow Condensed'}
            onChange={onChange}
          >
            {SPOTLIGHT_FONTS.map((f) => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginTop: '10px' }}>
          <label htmlFor="spotlightLabelSize">
            Font Size
            <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
              ({formData.spotlightLabelSize ?? 56}px)
            </span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Smaller</span>
            <input
              id="spotlightLabelSize"
              name="spotlightLabelSize"
              type="range"
              min={24}
              max={80}
              step={2}
              value={formData.spotlightLabelSize ?? 56}
              onChange={onChange}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Larger</span>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="spotlightLabelX">Label Position — Left / Right</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
            <input
              id="spotlightLabelX"
              name="spotlightLabelX"
              type="range"
              min={100}
              max={980}
              step={10}
              value={formData.spotlightLabelX ?? 540}
              onChange={onChange}
              style={{ flex: 1 }}
            />
            <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="spotlightLabelColor">Label Color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              id="spotlightLabelColor"
              name="spotlightLabelColor"
              type="color"
              value={formData.spotlightLabelColor || '#ffffff'}
              onChange={onChange}
              style={{ width: '48px', height: '36px', padding: '2px', borderRadius: '4px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '13px', color: '#4a5568' }}>{formData.spotlightLabelColor || '#ffffff'}</span>
          </div>
        </div>
      </div>

      {/* Dominant Image Upload */}
      <div className="form-group">
        <label>
          Player Photo (Optional)
          <span
            style={{
              fontSize: '12px',
              color: '#718096',
              fontWeight: 'normal',
              marginLeft: '8px',
            }}
          >
            (Upload action shot or portrait)
          </span>
        </label>
        {dominantImageCropping ? (
          <InlineCropper
            imageSrc={dominantImageTempImage}
            onCropComplete={onDominantImageCropComplete}
            onCancel={onDominantImageCropCancel}
          />
        ) : dominantImagePreview ? (
          <>
            <style>{`@keyframes bg-removal-spin { to { transform: rotate(360deg); } }`}</style>
            <div className="background-photo-preview" style={{ position: 'relative' }}>
              <img src={dominantImagePreview} alt="Player" />
              {isRemovingDominant && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.65)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'inherit',
                    zIndex: 10,
                  }}
                >
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      border: '3px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'bg-removal-spin 0.75s linear infinite',
                    }}
                  />
                  <p style={{ color: '#fff', fontSize: '13px', margin: '10px 0 0' }}>
                    Removing background…
                  </p>
                </div>
              )}
              <button
                type="button"
                className="btn-edit-photo-overlay"
                onClick={onEditDominantImageCrop}
                disabled={saving || downloading || isRemovingDominant}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn-remove-background-photo"
                onClick={onRemoveDominantImage}
                disabled={saving || downloading || isRemovingDominant}
              >
                ✕ Remove
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={canUseCutouts ? onRemoveDominantBg : undefined}
                disabled={!canUseCutouts || isRemovingDominant || saving || downloading}
                style={
                  !canUseCutouts
                    ? { opacity: 0.5, cursor: 'not-allowed', marginTop: '8px', width: '100%' }
                    : { marginTop: '8px', width: '100%' }
                }
              >
                {isRemovingDominant
                  ? 'Removing background…'
                  : canUseCutouts
                    ? '✂️ Remove Background'
                    : '🔒 Remove Background — Pro feature'}
              </button>
              {dominantBgRemovalError && (
                <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '4px' }}>
                  {dominantBgRemovalError}
                </p>
              )}
              {originalDominantImage && cutoutDominantImage && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    type="button"
                    className={activeDominantImageVersion === 'original' ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setActiveDominantImageVersion('original')}
                    style={{ flex: 1 }}
                    disabled={isRemovingDominant}
                  >
                    Original
                  </button>
                  <button
                    type="button"
                    className={activeDominantImageVersion === 'cutout' ? 'btn-primary' : 'btn-secondary'}
                    onClick={() => setActiveDominantImageVersion('cutout')}
                    style={{ flex: 1 }}
                    disabled={isRemovingDominant}
                  >
                    ✂️ Cutout
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <label htmlFor="highlight-dominant-image" className="background-photo-upload-btn">
            <span>📸 Upload Player Photo</span>
            <input
              id="highlight-dominant-image"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={onDominantImageChange}
              style={{ display: 'none' }}
              disabled={saving || downloading}
            />
          </label>
        )}
      </div>

      {/* Image Controls — Pro only */}
      {dominantImagePreview && canUseCutouts && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Image Controls
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageX">Image Position — Left / Right</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
              <input
                id="dominantImageX"
                name="dominantImageX"
                type="range"
                min={-100}
                max={200}
                step={5}
                value={formData.dominantImageX ?? 50}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageY">Image Position — Up / Down</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Up</span>
              <input
                id="dominantImageY"
                name="dominantImageY"
                type="range"
                min={-100}
                max={200}
                step={5}
                value={formData.dominantImageY ?? 50}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Down</span>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="dominantImageScale">
              Image Size
              <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                ({formData.dominantImageScale ?? 100}%)
              </span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Smaller</span>
              <input
                id="dominantImageScale"
                name="dominantImageScale"
                type="range"
                min={40}
                max={130}
                step={5}
                value={formData.dominantImageScale ?? 100}
                onChange={onChange}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Larger</span>
            </div>
          </div>
        </div>
      )}
      {dominantImagePreview && !canUseCutouts && (
        <p style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>
          🔒 Upgrade to Pro to reposition the player photo
        </p>
      )}

      {/* Background Section */}
      <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', marginTop: '8px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#4a5568', marginBottom: '12px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Background
        </div>
        <BackgroundStyleSelector
          backgroundStyle={backgroundStyle}
          teamColors={teamColors}
          onBackgroundStyleChange={onBackgroundStyleChange}
        />

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            type="button"
            className={bgMode === 'team-colors' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => { setBgMode('team-colors'); if (backgroundPhotoPreview) onRemoveBackground(); }}
          >
            Team Colors
          </button>
          <button
            type="button"
            className={bgMode === 'upload' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => setBgMode('upload')}
          >
            Upload Photo
          </button>
          <button
            type="button"
            className={bgMode === 'pexels' ? 'btn-primary' : 'btn-secondary'}
            style={{ flex: 1, padding: '8px 4px', fontSize: '12px' }}
            onClick={() => { setBgMode('pexels'); if (!pexelsResults.length) searchPexels('baseball stadium'); }}
          >
            Browse Free
          </button>
        </div>

        {bgMode === 'upload' && (
          <div className="form-group">
            {backgroundCropping ? (
              <InlineCropper
                imageSrc={backgroundTempImage}
                onCropComplete={onBackgroundCropComplete}
                onCancel={onBackgroundCropCancel}
              />
            ) : backgroundPhotoPreview && !backgroundPhotoPreview.includes('pexels.com') ? (
              <div className="background-photo-preview">
                <img src={backgroundPhotoPreview} alt="Background" />
                <button type="button" className="btn-edit-photo-overlay" onClick={onEditBackground} disabled={saving || downloading}>
                  Edit
                </button>
                <button type="button" className="btn-remove-background-photo" onClick={onRemoveBackground} disabled={saving || downloading}>
                  ✕ Remove
                </button>
              </div>
            ) : (
              <label htmlFor="highlight-bg-photo" className="background-photo-upload-btn">
                <span>Upload Background Photo</span>
                <input
                  id="highlight-bg-photo"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={onBackgroundPhotoChange}
                  style={{ display: 'none' }}
                  disabled={saving || downloading}
                />
              </label>
            )}
          </div>
        )}

        {bgMode === 'pexels' && (
          <div className="form-group">
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <input
                type="text"
                value={pexelsQuery}
                onChange={(e) => setPexelsQuery(e.target.value)}
                placeholder="baseball stadium, field…"
                style={{ flex: 1 }}
              />
              <button type="button" onClick={handlePexelsSearch} className="btn-secondary" disabled={pexelsLoading} style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                {pexelsLoading ? '…' : 'Search'}
              </button>
            </div>

            {backgroundPhotoPreview?.includes('pexels.com') && (
              <div className="brag-card-unsplash-active">
                <img src={backgroundPhotoPreview} alt="Selected background" crossOrigin="anonymous" />
                <button type="button" className="brag-card-remove-btn" onClick={onRemoveBackground}>
                  Remove
                </button>
              </div>
            )}

            {!pexelsLoading && pexelsResults.length === 0 && (
              <p style={{ fontSize: '13px', color: '#718096', marginTop: '8px' }}>No photos found.</p>
            )}

            {pexelsResults.length > 0 && (
              <div className="brag-card-unsplash-grid">
                {pexelsResults.map((photo) => (
                  <button
                    key={photo.id}
                    type="button"
                    className={`brag-card-unsplash-thumb${backgroundPhotoPreview === photo.src.large ? ' selected' : ''}`}
                    onClick={() => onSetBackgroundFromUrl(photo.src.large)}
                    title={photo.photographer || ''}
                  >
                    <img src={photo.src.medium} alt={photo.alt || ''} crossOrigin="anonymous" />
                  </button>
                ))}
              </div>
            )}

            {pexelsResults.length > 0 && pexelsResults.length < pexelsTotalResults && (
              <button
                type="button"
                className="brag-card-search-btn brag-card-load-more-btn"
                onClick={handlePexelsLoadMore}
                disabled={pexelsLoadingMore}
                style={{ marginTop: '8px', width: '100%' }}
              >
                {pexelsLoadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}

            <p style={{ fontSize: '11px', color: '#718096', marginTop: '8px' }}>
              Photos: <a href="https://www.pexels.com" target="_blank" rel="noreferrer">Pexels</a>
            </p>
          </div>
        )}

        {bgMode !== 'team-colors' && backgroundPhotoPreview && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Image Position &amp; Scale
            </div>
            <div className="form-group">
              <label htmlFor="bgImageX">Left / Right</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Left</span>
                <input
                  id="bgImageX"
                  name="bgImageX"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.bgImageX ?? 50}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Right</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="bgImageY">Up / Down</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Up</span>
                <input
                  id="bgImageY"
                  name="bgImageY"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.bgImageY ?? 50}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Down</span>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="bgImageScale">
                Zoom
                <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>
                  ({formData.bgImageScale ?? 100}%)
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>Out</span>
                <input
                  id="bgImageScale"
                  name="bgImageScale"
                  type="range"
                  min={10}
                  max={200}
                  step={5}
                  value={formData.bgImageScale ?? 100}
                  onChange={onChange}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: '11px', color: '#718096', flexShrink: 0 }}>In</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default HighlightFields;
